import { u } from "../lib/images";

// Colour system from the official logo (Founding Prospectus):
// pink #FF2F89 · amber #FFC300 · teal #43D2FF
// accent = exact logo colour (dots, borders, frames, fills)
// accentText = deepened same-hue variant for legible text on cream
// onAccent = text colour placed on top of the exact accent colour
export const pillars = {
  animals: {
    key: "animals",
    name: "Animals",
    formalName: "Animal Welfare",
    accent: "#43D2FF",
    accentText: "#0B7A8C",
    onAccent: "#1C1917",
    eyebrow: "Pillar 01 — Animals",
    headline: "Compassion, on four legs and two.",
    shortDescription:
      "Compassion for animals through feeding, veterinary support, sterilisation and vaccination drives, and partnerships with the people already doing the work.",
    philosophy:
      "This is where PilCoo began — with two dogs who asked for nothing and gave everything. Animal welfare is how we return that to the creatures who share our streets and homes: the ones who go hungry, get hurt, or are simply overlooked. We work alongside the feeders, rescuers, shelters and veterinary professionals who know the work best — strengthening what exists rather than duplicating it.",
    cta: "Explore Animals",
    image: u("https://images.pexels.com/photos/6571217/pexels-photo-6571217.jpeg", 1400),
    imageAlt: "A man feeding a group of street dogs beside a colourful truck in Kollam, India",
    secondaryImage: "/photos/animals-inset.webp",
    secondaryImageAlt: "An indie dog being given drinking water from a bottle",
    statusNote:
      "The PilCoo Foundation does not plan to build or operate a physical shelter at launch. The model is to support existing ecosystems — shelters, rescuers and veterinary professionals — rather than duplicate them.",
    focusAreas: [
      { title: "Street care & feeding routes", text: "Regular, organised feeding so street animals eat consistently — not just when someone remembers." },
      { title: "Clean water access", text: "Simple, sustained access to clean drinking water for street animals, especially through the hot months." },
      { title: "Sterilisation & vaccination drives", text: "Humane population control and disease prevention, run with veterinary professionals." },
      { title: "Emergency rescue & treatment", text: "Helping an animal in distress reach the right hands quickly, and supporting treatment costs where the community can." },
      { title: "Adoption & foster awareness", text: "Making the case for adoption and fostering, and helping shelters tell their animals' stories." },
      { title: "Shelter, rescuer & vet partnerships", text: "A trusted network of veterinary professionals and shelters that advises and guides the work." },
    ],
    buildingToward: [
      "A foster & adoption network",
      "Children's education on animal care and strays",
      "Disease awareness in the communities we serve",
    ],
  },
  people: {
    key: "people",
    name: "People",
    formalName: "Mental Health",
    accent: "#FF2F89",
    accentText: "#D6106A",
    onAccent: "#F7F5F0",
    eyebrow: "Pillar 02 — People",
    headline: "It's okay to talk. It's okay to ask.",
    shortDescription:
      "Creating conversations, awareness and community initiatives around mental health and wellbeing — and reducing the stigma of asking for support.",
    philosophy:
      "The people who carry the most are often the last to be asked how they're doing — and far too often, they are men. This pillar is for them, and for anyone struggling quietly: to make asking for help ordinary, and to make sure no one carries it alone.",
    cta: "Explore People",
    image: "/photos/people-main.webp",
    imageAlt: "A group of contemporary Indian adults in a warm group conversation",
    secondaryImage: "/photos/people-inset.webp",
    secondaryImageAlt: "An older and a younger person's hands held in support",
    statusNote:
      "The PilCoo Foundation is not a therapy provider, a healthcare provider, a crisis service or a medical authority. We create conversations and awareness — and point toward credible professionals when someone needs more.",
    focusAreas: [
      { title: "Community meetups & safe-space circles", text: "Small, facilitated gatherings where people can speak — or simply listen — without judgement." },
      { title: "Expert-led sessions", text: "Sessions with credible professionals on understanding and supporting mental wellbeing." },
      { title: "Reducing stigma", text: "Stories and conversations that make asking for support an act of strength, not shame." },
      { title: "Grief & loss awareness", text: "Making room for honest conversation about loss — something every community carries quietly." },
      { title: "Counsellor referrals", text: "Signposting toward verified professionals, helplines and organisations when someone needs more than a conversation." },
      { title: "Wellbeing awareness", text: "Initiatives that bring people together and reduce isolation — walks, conversations, shared activity." },
    ],
    buildingToward: [
      "Men's meetups & AMAs",
      "Subsidised counselling access",
      "School & college programmes and children's emotional education",
      "Rural outreach",
      "Human-animal bond sessions",
    ],
  },
  nature: {
    key: "nature",
    name: "Nature",
    formalName: "Nature Conservation",
    accent: "#FFC300",
    accentText: "#8F6D00",
    onAccent: "#1C1917",
    eyebrow: "Pillar 03 — Nature",
    headline: "Start with the street you live on.",
    shortDescription:
      "Helping communities participate in local environmental action — clean-ups, planting, and practical sustainable habits.",
    philosophy:
      "The same care we give animals and one another, we owe to the world that holds us all. Nature conservation is our work to protect and restore the green and blue spaces around us — and to help people remember that they belong to them, and to it.",
    cta: "Explore Nature",
    image: u("https://images.pexels.com/photos/7656723/pexels-photo-7656723.jpeg", 1400),
    imageAlt: "A person planting a sapling at sunset",
    secondaryImage: "/photos/nature-inset.webp",
    secondaryImageAlt: "Hands cradling a young sapling in soil",
    statusNote:
      "Practical participation over performative sustainability — small, repeated local action, done properly and documented honestly.",
    focusAreas: [
      { title: "Community clean-up drives", text: "Neighbourhood clean-ups where anyone can show up, pick up, and see the difference immediately." },
      { title: "Tree plantation", text: "Planting done properly — right species, right place, and a plan to keep saplings alive." },
      { title: "Waste & plastic awareness", text: "Practical campaigns that help households and local businesses cut everyday plastic." },
      { title: "Water conservation", text: "Awareness and local action around the water bodies and resources a neighbourhood shares." },
      { title: "Habitat restoration", text: "Supporting the restoration of small local spaces: parks, lake edges, forgotten corners." },
      { title: "Wildlife education & nature walks", text: "Helping people — especially children — meet the living world on their own streets." },
    ],
    buildingToward: [
      "Lake & water-body revival",
      "A first tree-plantation season with schools and local communities",
      "School eco-clubs",
    ],
  },
};

export const pillarOrder = ["animals", "people", "nature"];
